/**
 * HoraClockView — IChartView adapter for Hora Clock
 *
 * Encapsulates: data preparation + CircularRingRenderer lifecycle
 */

import * as d3 from 'd3';
import type { IChartView } from '../ChartViewManager';
import type { ChartViewMode } from '../../../../../../types/ChartViewTypes';
import { CircularRingRenderer } from '../../modules/CircularRingRenderer';
import type { HoraClockHourMode } from '../../modules/CircularRingRenderer';
import { chakraDataService } from '../../../../../../services/ChakraDataService';
import { devWarn } from '../../../../../../utils/devLog';

export class HoraClockView implements IChartView {
    readonly viewId: ChartViewMode = 'horaClock';

    private chartId: string;
    private renderer: CircularRingRenderer | null = null;
    private rendered: boolean = false;
    private hourMode: HoraClockHourMode = 12;

    constructor(chartId: string) {
        this.chartId = chartId;
        // Restore hourMode from settings
        try {
            const ctx = window.multipleChartsContext;
            const settings = (ctx?.charts?.get(chartId) as any)?.settings;
            if (settings?.horaClockHourMode === 24) {
                this.hourMode = 24;
            }
        } catch { /* ignore */ }
    }

    getHourMode(): HoraClockHourMode {
        return this.hourMode;
    }

    setHourMode(mode: HoraClockHourMode): void {
        this.hourMode = mode;
    }

    async activate(options?: Record<string, any>): Promise<void> {
        const data = chakraDataService.prepareHoraClockData(this.chartId, this.hourMode);
        if (!data) {
            devWarn(`HoraClockView[${this.chartId}]: No hora clock data available`);
            return;
        }
        if (options?.designOverride) {
            data.designConfig = options.designOverride;
        }

        const layer = this.getLayer();
        if (!layer) return;

        if (!this.renderer) {
            this.renderer = new CircularRingRenderer(this.chartId, 'hora', layer);
        }
        this.renderer.renderFromData(data);
        this.renderer.show();
        this.rendered = true;
    }

    deactivate(): void {
        this.renderer?.hide();
    }

    destroy(): void {
        this.renderer?.destroy();
        this.renderer = null;
        this.rendered = false;
    }

    isRendered(): boolean {
        return this.rendered;
    }

    private getLayer(): d3.Selection<SVGGElement, unknown, HTMLElement, any> | null {
        const sel = d3.select<SVGGElement, unknown>(`#hora-clock-container-${this.chartId}`);
        return sel.empty() ? null : sel;
    }
}
